import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { useDispatch, connect } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import CartCounter from '../CartCounter';
import OutlinedInput from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { logError, addToCartData, removeToCartData } from '@actions/userActions';
// import { mergeCartFunc, getRemoteCartData, updateRemoteCartData } from '@src/utilities/cart';
import { hasPermission, generateUniqueCode, getRoomLabelText } from '@src/utilities/common';
import { submitOrder } from '@src/utilities/order';
import { KOT_SESSION_CART_KEY, isKotSessionCartKey, pickKotSessionForMerge } from '@utilities/kotSession';
import LinearProgress from '@mui/material/LinearProgress';
import PrintKOTPrint from '@components/Print/PrintKOTPrint';
import OrderServiceTypeToggle from '@components/OrderServiceTypeToggle';
import {
    DEFAULT_ORDER_SERVICE_TYPE,
    normalizeOrderServiceType,
    type OrderServiceType,
} from '@constants/orderServiceType';
import moment from 'moment';
import { ImagesGS } from '@src/constants/common';
import { Typography } from '@mui/material';
import MergeIcon from '@mui/icons-material/Merge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import {useOnlineStatus}  from '@src/app/hooks';
import './Cart.scss';

const CART_INPUTS_STORAGE_KEY = 'cartInputsByTable';

export const getGstData = (cart: any, isGstBill: boolean, gstRate: number, _totalCost: number) => {
    const gstData: any = {};
    let totalGSTCost = 0;

    if (isGstBill && cart) {
        Object.entries(cart).forEach(([key, cartItem]: any) => {
            if (isKotSessionCartKey(key)) return;
            if (!gstData[cartItem?.customGST || gstRate || 0]) {
                gstData[cartItem?.customGST || gstRate || 0] = 0;
            }
            if (cartItem?.selected) {
                gstData[cartItem?.customGST || gstRate || 0] += +(Number(cartItem?.selected * cartItem?.selectedVariants?.value) * Number((cartItem?.customGST || gstRate || 0) / 100)).toFixed(2);
                totalGSTCost += +(Number(cartItem?.selected * cartItem?.selectedVariants?.value) * Number((cartItem?.customGST || gstRate || 0) / 100)).toFixed(2);
            }
        });
    }
    return {
        gstData,
        totalGSTCost
    }
}

function Cart({ type, key: _key, item: _item, cart, toggle, user, selectedRestaurant, tableNumber = "table-1", isClassExtra, allTabs, selectedRestId, roles, userPermissions }: any) {
    const gstRate = selectedRestaurant?.gstRate;
    const serviceRate = selectedRestaurant?.serviceRate;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [totalCost, setTotalCost] = useState(0);
    const [selectedPaymentType] = useState("cash");
    const [discount, setDiscount] = useState(0);
    const [name, setName]: any = useState('');
    const [phoneNum, setPhoneNum]: any = useState('');
    const [billingName, setbillingName]: any = useState('');
    const [gstNumber, setGstNumber]: any = useState('');
    const [billingAddress, setBillingAddress]: any = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const params = useParams();
    const [checkedGST] = useState(true);
    const canPrintKOT = hasPermission(userPermissions, "printKOT", "read");
    const isOnline = useOnlineStatus();
    const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
    const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
    const [destTableNumber, setDestTableNumber] = useState('');
    const [serviceType, setServiceType] = useState<OrderServiceType>(DEFAULT_ORDER_SERVICE_TYPE);
    const tableScopedStorageKey = `${params?.restaurantId || selectedRestId || 'default'}::${tableNumber || 'table-1'}`;

    const getCartInputsStorage = () => {
        try {
            return JSON.parse(localStorage.getItem(CART_INPUTS_STORAGE_KEY) || '{}');
        } catch (_error) {
            return {};
        }
    };

    useEffect(() => {
        let totalCostDummy = 0;
        !!cart[tableNumber] && Object.entries(cart[tableNumber]).forEach(([key, val]: any) => {
            if (isKotSessionCartKey(key)) return;
            if (val?.selectedVariants?.value) {
                totalCostDummy += val?.selectedVariants?.value * val?.selected;
            }
        });
        setTotalCost(totalCostDummy);
    }, [cart, tableNumber]);

    useEffect(() => {
        const allStoredInputs = getCartInputsStorage();
        const currentTableInputs = allStoredInputs?.[tableScopedStorageKey] || {};

        setPhoneNum(currentTableInputs?.phoneNum || '');
        setDiscount(Number(currentTableInputs?.discount) || 0);
        setName(currentTableInputs?.name || '');
        setbillingName(currentTableInputs?.billingName || currentTableInputs?.name || '');
        setGstNumber(currentTableInputs?.gstNumber || '');
        setBillingAddress(currentTableInputs?.billingAddress || '');
        setServiceType(normalizeOrderServiceType(currentTableInputs?.serviceType));
    }, [tableScopedStorageKey]);

    useEffect(() => {
        const allStoredInputs = getCartInputsStorage();
        allStoredInputs[tableScopedStorageKey] = {
            phoneNum: phoneNum || '',
            discount: Number(discount) || 0,
            name: name || '',
            billingName: billingName || '',
            gstNumber: gstNumber || '',
            billingAddress: billingAddress || '',
            serviceType,
        };
        localStorage.setItem(CART_INPUTS_STORAGE_KEY, JSON.stringify(allStoredInputs));
    }, [phoneNum, discount, name, billingName, gstNumber, billingAddress, serviceType, tableScopedStorageKey]);

    const handleValueUpdated = (key: string, val: object, final: number, itemDescription?: string) => {
        if (final === 0) {
            dispatch(addToCartData({ [key]: null }, tableNumber, params?.restaurantId));
        } else {
            const updatedItem = { ...val, selected: final };
            if (itemDescription !== undefined) {
                (updatedItem as any).itemDescription = itemDescription || '';
            }
            dispatch(addToCartData({ [key]: updatedItem }, tableNumber, params?.restaurantId));
        }
    }

    const handleDescriptionChange = (key: string, val: any, description: string) => {
        const updatedItem = { ...val, itemDescription: description };
        dispatch(addToCartData({ [key]: updatedItem }, tableNumber, params?.restaurantId));
    }

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedAccordion(isExpanded ? panel : false);
    }

    const clearCurrentTableCartInputs = () => {
        const allStoredInputs = getCartInputsStorage();
        delete allStoredInputs[tableScopedStorageKey];
        localStorage.setItem(CART_INPUTS_STORAGE_KEY, JSON.stringify(allStoredInputs));
        setPhoneNum('');
        setDiscount(0);
        setName('');
        setbillingName('');
        setGstNumber('');
        setBillingAddress('');
        setServiceType(DEFAULT_ORDER_SERVICE_TYPE);
    };

    const handleCheckout = async () => {
        setIsLoading(true);
        // console.log('final cart items--', cart, user);
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const isGstBill = !!checkedGST;
        const { gstData, totalGSTCost } = getGstData(cart[tableNumber], isGstBill, gstRate, totalCost);

        if (userData?.phoneNumber) {
            // setOpen(true);
            const gstRate: number = !!checkedGST ? +selectedRestaurant?.gstRate : 0;
            const serviceRate: number = !!checkedGST ? +selectedRestaurant?.serviceRate : 0;

            const serviceBill: number = Number((totalCost * serviceRate / 100).toFixed(2));

            const orderDetails = {
                uoId: generateUniqueCode(6),
                cart: cart[tableNumber],
                tableNumber,
                selectedPayment: selectedPaymentType || '',
                user: {
                    // accessToken: user?.accessToken ?? 'NA',
                    email: user?.email ?? 'NA',
                    name: user?.name ?? 'NA',
                    phone: user?.phone ?? 'NA',
                    phoneNumber: user?.phoneNumber ?? 'NA',
                    role: roles[selectedRestId]?.role ?? 'NA',
                    uid: user?.uid ?? 'NA',
                },
                userId: user?.phoneNumber,
                restaurant: {
                    address: selectedRestaurant?.address,
                    docId: selectedRestaurant?.docId,
                    foodType: selectedRestaurant?.foodType,
                    gstIn: selectedRestaurant?.gstIn,
                    gstRate: !!checkedGST ? selectedRestaurant?.gstRate : 0,
                    serviceRate: !!checkedGST ? selectedRestaurant?.serviceRate : 0,
                    location: selectedRestaurant?.location,
                    name: selectedRestaurant?.name,
                    ownerId: selectedRestaurant?.ownerId,
                    ownerName: selectedRestaurant?.ownerName,
                    ownerPhone: selectedRestaurant?.ownerPhone,
                },
                restaurantSAC: selectedRestaurant?.restaurantSAC || '',
                restaurantId: params?.restaurantId,
                isGstBill: !!checkedGST,
                customerPhone: phoneNum || '',
                customerName: name || '',
                customerGstNumber: gstNumber || '',
                customerBillingAddress: billingAddress || '',
                gstRate,
                serviceRate,
                discount,
                itemsCost: totalCost || '',
                orderStatus: { label: 'Pending', value: 'pending' },
                gstBill: totalGSTCost || '',
                serviceBill,
                gstData: gstData || {},
                billingAddress: billingAddress || '',
                billingName: billingName || '',
                gstNumber: gstNumber || '',
                totalCost: Math.round(totalCost + Math.ceil(totalGSTCost) + totalCost * serviceRate / 100 - discount),
                createdAt: moment().utc().valueOf(),
                serviceType,
            }
            const isOrderSubmitted = await submitOrder(orderDetails, dispatch, navigate, params?.restaurantId, tableNumber, type);
            if (isOrderSubmitted) {
                clearCurrentTableCartInputs();
            }
        } else {
            navigate(`/login?redirect=/restaurant/${params?.restaurantId}`);
        }
        setIsLoading(false);
    }

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNum(event.target.value);
    }

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
        setbillingName(event.target.value);
    }

    const handleGSTChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGstNumber(event.target.value);
    }

    const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setBillingAddress(event.target.value);
    }

    const isGstBill = !!checkedGST;
    const { gstData, totalGSTCost } = getGstData(cart[tableNumber], isGstBill, gstRate, totalCost);
    const getMergedTables = (destObj: any, srcObj: any) => {
        if (!Object.keys(destObj || {}).length) {
            return srcObj;
        }
        if (!Object.keys(srcObj || {}).length) {
            return destObj;
        }
        let newObj = { ...destObj };
        Object.entries(srcObj).forEach(([itemKey, itemDetails]: any) => {
            if (isKotSessionCartKey(itemKey)) return;
            if (newObj[itemKey]) {
                newObj[itemKey] = {
                    ...newObj[itemKey],
                    selected: newObj[itemKey]["selected"] + (!!itemDetails ? itemDetails["selected"] : 0),
                }
            } else {
                newObj[itemKey] = itemDetails;
            }
        });

        return newObj;
    }
    const handleMergeTable = (selectedTableNumber: string) => {
        let destTableNumber: any = selectedTableNumber;
        let nweAllTabs: any = [];
        Object.entries(allTabs).forEach(([tab, count]: any) => {
            for (let i = 1; i <= count; i++) {
                nweAllTabs.push(`${tab}-${i}`);
            }
        });
        let found = nweAllTabs.find((str: string) => {
            if (str?.toLowerCase() === destTableNumber?.toLowerCase()) {
                destTableNumber = str;
                return true;
            }
            return false;
        });

        if (found && destTableNumber?.toLowerCase() !== tableNumber?.toLowerCase()) {
            if (params?.restaurantId && tableNumber && destTableNumber) {
                let mergedData = getMergedTables(cart[destTableNumber], cart[tableNumber]);
                if (mergedData) {
                    mergedData[KOT_SESSION_CART_KEY] = pickKotSessionForMerge(
                        cart[destTableNumber],
                        cart[tableNumber],
                        destTableNumber
                    );
                    dispatch(addToCartData({ ...mergedData }, destTableNumber, params?.restaurantId));
                    dispatch(removeToCartData({}, tableNumber, params?.restaurantId));
                }
            }

            dispatch(logError({ severity: 'success', message: `Cart items moved in ${destTableNumber?.toUpperCase()} ` }))
        } else {
            dispatch(logError({ severity: 'error', message: `${destTableNumber?.toUpperCase()} doesn't exists!` }))
        }
    }

    const availableTables = Object.entries(allTabs).reduce((acc: string[], [tab, count]: any) => {
        for (let i = 1; i <= count; i++) {
            acc.push(`${tab}-${i}`);
        }
        return acc;
    }, []);

    const selectableTables = availableTables.filter((table) => table?.toLowerCase() !== tableNumber?.toLowerCase());

    const openMergeDialog = () => {
        setDestTableNumber('');
        setIsMergeDialogOpen(true);
    };

    const closeMergeDialog = () => {
        setIsMergeDialogOpen(false);
        setDestTableNumber('');
    };

    const confirmMergeTable = () => {
        if (!destTableNumber) {
            dispatch(logError({ severity: 'error', message: 'Please select a table.' }));
            return;
        }
        handleMergeTable(destTableNumber);
        closeMergeDialog();
    };
    // TODO: Enable sync cart with cloud - IN Progress
    // const handleSyncCart = () => {
    //     if(params?.restaurantId){
    //         try{
    //             setIsSyncing(true);
    //             // console.log('in sync');
    //             let localCart:any = JSON.parse(localStorage?.getItem("cartData") || '{}');
    //             let remoteCart = {};
    //             let mergedCart = {};
    //             getRemoteCartData(params?.restaurantId, dispatch).then((data: any) => {
    //                 console.log('data remote=', data);
    //                 remoteCart = {...data};
    //                 mergedCart = mergeCartFunc(params?.restaurantId ? localCart[params?.restaurantId] : {}, remoteCart);
    //                 // console.log('localCart, remoteCart, mergedCart', localCart, remoteCart, mergedCart);
    //                 // dispatch(syncCartData(mergedCart, params?.restaurantId));
    //                 updateRemoteCartData(params?.restaurantId, mergedCart, dispatch).then((data: any) => {
    //                     setIsSyncing(false);
    //                 });
    //             });
    //         } catch (error: any){
    //             console.error("Error syncing cart: ", error);
    //             dispatch(logError({ severity: 'error', message: error?.message || 'NA' }));
    //             setIsSyncing(false);
    //         }
    //     }
    // }

    return (
        <div className={`cart-main-wrap ${toggle && 'animateCart'}`}>
            <div className={`cart-main ${type} ${isClassExtra && "extra"}`}>
                <div className='cart-header'>
                    <Typography textTransform="capitalize" display="inline-block" fontWeight="500" textAlign="center"> {getRoomLabelText(tableNumber?.split("-")[0], parseInt(tableNumber?.split("-")[1]))}</Typography>
                    {/* {!isSyncing &&<IconButton aria-label="sync" onClick={() => handleSyncCart()} color="secondary">
                        <Typography display="inline-block" textAlign="center">Sync</Typography>
                        <CloudSyncIcon />
                    </IconButton>}
                    {!!isSyncing && <Loader small />}  */}
                    <Button title='Merge' onClick={openMergeDialog} aria-label="add an alarm" color="secondary" variant="outlined">
                        <Typography variant="body1">Merge</Typography>
                        <MergeIcon />
                    </Button>
                    {/* {canReadTestBilling && <div>Mode: <Switch color='secondary' checked={!!checkedGST} onChange={handleChangeSwitch} {...label} /></div>} */}
                </div>
                <Dialog open={isMergeDialogOpen} onClose={closeMergeDialog} fullWidth maxWidth="xs">
                    <DialogTitle>Move cart to table</DialogTitle>
                    <DialogContent>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            margin="normal"
                            label="Destination table"
                            value={destTableNumber}
                            onChange={(event) => setDestTableNumber(event.target.value)}
                        >
                            {selectableTables.map((table) => (
                                <MenuItem key={table} value={table}>
                                    {getRoomLabelText(table?.split("-")[0], parseInt(table?.split("-")[1]))}
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeMergeDialog} color="secondary">Cancel</Button>
                        <Button onClick={confirmMergeTable} color="primary" variant="outlined">Move</Button>
                    </DialogActions>
                </Dialog>
                {
                    !!cart[tableNumber] && Object.entries(cart[tableNumber]).map(([key, val]: any, _index: any) => {
                        if (isKotSessionCartKey(key)) return null;
                        if (!!val) {
                            const cartItemContent = (
                                <Box className='cart-item-wrap' sx={{ flexGrow: 1, width: '100%' }}>
                                    <Grid container spacing={2} columns={16}>
                                        <Grid item xs={7}>
                                            <span className='cart-item-img'>
                                                <img src={val.image || ImagesGS.itemLightPlaceholder} title={val.name} alt="" />
                                            </span>
                                            <div className='cart-item-text'>
                                                <p>{(val.name).replaceAll("-", " ")}</p>
                                                <span>{val.selectedVariants?.label}</span>
                                            </div>
                                        </Grid>
                                        <Grid 
                                            item 
                                            xs={6} 
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            <CartCounter initialValue={cart[tableNumber][key].selected} onValueUpdate={(final: any) => handleValueUpdated(key, val, final, val.itemDescription)} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            ₹{val?.selectedVariants?.value * val.selected}
                                        </Grid>
                                    </Grid>
                                </Box>
                            );

                            if (canPrintKOT) {
                                return (
                                    <Accordion 
                                        key={key + '-' + val.name} 
                                        expanded={expandedAccordion === key}
                                        onChange={handleAccordionChange(key)}
                                        sx={{ 
                                            boxShadow: 'none',
                                            '&:before': { display: 'none' },
                                            '&.Mui-expanded': { margin: 0 },
                                            borderBottom: '1px solid #e0e0e0'
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ 
                                                padding: '8px 16px',
                                                minHeight: 'auto',
                                                '&.Mui-expanded': { minHeight: 'auto' },
                                                '& .MuiAccordionSummary-content': { margin: '0', '&.Mui-expanded': { margin: '0' } }
                                            }}
                                        >
                                            {cartItemContent}
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ padding: '0 16px 5px' }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={2}
                                                label="KOT Instructions"
                                                placeholder="Add instructions for kitchen..."
                                                value={val.itemDescription || ''}
                                                onChange={(e) => handleDescriptionChange(key, val, e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                onClick={(e) => e.stopPropagation()}
                                                sx={{ 
                                                    '& .MuiOutlinedInput-root': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            } else {
                                return (
                                    <Box key={key + '-' + val.name} className='cart-item-wrap' sx={{ flexGrow: 1 }}>
                                        {cartItemContent}
                                    </Box>
                                );
                            }
                        }
                        return null;
                    })
                }
            </div>
            <div className='checkout-btn'>
                <span className='hr-line'></span>
                <div className='textCartChargeWrap'>
                    <p>Cost of Items: ₹{(totalCost).toFixed(2)}</p>
                    {/* <p>Discount: ₹{discount > 0 && discount}</p> */}
                    {!!serviceRate && <p>Service Charges({(!!checkedGST ? serviceRate : 0)}%): ₹{(totalCost * (!!checkedGST ? serviceRate : 0) / 100).toFixed(2)}</p>}
                    {
                        Object.keys(gstData).map((gstVal: any) => {
                            if (!!gstData[gstVal]) {
                                return <p key={gstVal}>GST({gstVal}%): ₹{gstData[gstVal]}</p>
                            }
                            return null;
                        }
                        )
                    }
                </div>
                <span className='hr-line'></span>
                <div>
                    {!isOnline && <span className={`onlineStatue ${!isOnline && 'offline'}`}>{isOnline ? "" : "Internet ❌"}</span>}
                    <p className='large'>Total: ₹{(Math.round(totalCost + Math.ceil(totalGSTCost) + totalCost * (!!checkedGST ? serviceRate : 0) / 100 - (discount > 0 ? discount : 0)))}</p>
                </div>
                <span className='hr-line'></span>

                {isLoading && <LinearProgress color="secondary" />}
                {roles && selectedRestId && ['admin', 'manager', 'staff'].includes(roles[selectedRestId]?.role) &&
                    <div className='wrapSlider'>
                        <Grid className='cartInputs' container spacing={1} columns={12}>
                            {/* <Grid item xs={6}>
                                <Discount size="small" fullWidth value={discount} onChange={handleDiscount} />
                                </Grid> */}
                            <Grid item xs={selectedRestaurant?.permissions?.foodGST ? 6 : 12}>
                                <OutlinedInput size="small" fullWidth type='string' value={name} onChange={handleNameChange} placeholder="Name" />
                            </Grid>
                            { selectedRestaurant?.permissions?.foodGST &&
                                <Grid item xs={6}>
                                    <OutlinedInput size="small" fullWidth type='string' value={gstNumber} onChange={handleGSTChange} placeholder="GST" />
                                </Grid>
                            }
                            <Grid item xs={6}>
                                <OutlinedInput size="small" fullWidth type='string' value={billingAddress} onChange={handleAddressChange} placeholder="Address" />
                            </Grid>
                            <Grid item xs={6}>
                                <OutlinedInput size="small" fullWidth type="number" value={phoneNum} onChange={handlePhoneChange} placeholder="Phone#" />
                            </Grid>
                        </Grid>
                        {/* {restaurant?.permissions?.foodGST && <Grid className='cartInputs' container spacing={1} columns={12}>
            <Grid item xs={6}>
                <OutlinedInput fullWidth type='string' onChange={handleNameChange} placeholder="Name" />
            </Grid>
            <Grid item xs={6}>
                <OutlinedInput fullWidth type='string' onChange={handleGSTChange} placeholder="GST Number" />
            </Grid>
        </Grid>}
        {restaurant?.permissions?.foodGST && <Grid className='cartInputs' container spacing={1} columns={12}>
            <Grid item xs={6}>
                <OutlinedInput fullWidth type='string' onChange={handleAddressChange} placeholder="Address" />
            </Grid>
        </Grid>} */}
                    </div>
                }
                <div className="cart-service-type-wrap">
                    <OrderServiceTypeToggle
                        value={serviceType}
                        onChange={setServiceType}
                    />
                </div>
                <Grid className='cartInputs' container spacing={1} columns={12}>
                    {canPrintKOT && <Grid item xs={4}>
                        <PrintKOTPrint
                            item={cart[tableNumber]}
                            tableNumber={tableNumber}
                            resId={selectedRestId}
                            serviceType={serviceType}
                            source="pos-cart"
                        />
                    </Grid>}
                    <Grid item xs={canPrintKOT ? 8 : 12}>
                        <Button disabled={!isOnline || !totalCost || isLoading} onClick={handleCheckout} color="primary" variant="outlined">
                            Checkout
                        </Button>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
}

const mapStateToProps = (state: any) => {
    const { cart, common, user } = state;
    return {
        cart: (cart && cart[common?.selectedRestaurant?.docId]) || {},
        user: user?.user,
        selectedRestaurant: common?.selectedRestaurant,
        allTabs: common?.selectedRestaurant?.tabs || {},
        selectedRestId: common?.selectedRestaurant?.docId || "",
        roles: user?.user?.roles || {},
        userPermissions: user?.user?.roles ? {...common?.globalConfig?.permissions, ...(user?.user?.roles[common?.selectedRestaurant?.docId]?.permissions)} : {}
    }
}

export default connect(mapStateToProps)(Cart)
